/**
 * L10 visual identity — single source of truth for the per-section and
 * per-item-type icon + accent color used across the runner (section headers,
 * the shared item composer, the universal Create switcher, drop-to-issues
 * affordances). Centralizing it keeps the seven sections visually distinct and
 * consistent instead of each file hand-picking a color.
 *
 * Every accent carries BOTH light and dark treatments so callers never
 * hand-write `dark:` variants (the #1 source of dark-mode drift). Accents lean
 * on the Hickory brand olive/brown plus the Tailwind palette; the scorecard
 * variance system (`components/scorecard/utils/variance.ts`) is PROTECTED and
 * intentionally NOT re-skinned here.
 */

import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Coffee,
  Flag,
  Lightbulb,
  ListChecks,
  Mountain,
  Newspaper,
  Send,
} from "lucide-react";

import type { AgendaKind } from "@/api/l10";
import type { MeetingItemType } from "./composer/meeting-item";

export interface Accent {
  /** Icon + accent text color (light + dark). */
  fg: string;
  /** Soft tinted surface (light + dark) — section header band, chips. */
  soft: string;
  /** Border that pairs with `soft`. */
  border: string;
  /** Solid fill — active chips, status dots, the left identity bar. */
  solid: string;
}

export const ACCENTS = {
  amber: {
    fg: "text-amber-600 dark:text-amber-400",
    soft: "bg-amber-50 dark:bg-amber-950/40",
    border: "border-amber-200 dark:border-amber-900/50",
    solid: "bg-amber-500",
  },
  blue: {
    fg: "text-blue-600 dark:text-blue-400",
    soft: "bg-blue-50 dark:bg-blue-950/40",
    border: "border-blue-200 dark:border-blue-900/50",
    solid: "bg-blue-500",
  },
  olive: {
    fg: "text-[#587051] dark:text-[#9CB58C]",
    soft: "bg-[#587051]/10 dark:bg-[#587051]/25",
    border: "border-[#587051]/30 dark:border-[#587051]/40",
    solid: "bg-[#587051]",
  },
  forest: {
    fg: "text-[#4A5F4A] dark:text-[#8FB08C]",
    soft: "bg-[#4A5F4A]/10 dark:bg-[#4A5F4A]/25",
    border: "border-[#4A5F4A]/30 dark:border-[#4A5F4A]/40",
    solid: "bg-[#4A5F4A]",
  },
  brown: {
    fg: "text-[#705B4A] dark:text-[#C0AA98]",
    soft: "bg-[#705B4A]/10 dark:bg-[#705B4A]/25",
    border: "border-[#705B4A]/30 dark:border-[#705B4A]/40",
    solid: "bg-[#705B4A]",
  },
  rose: {
    fg: "text-rose-600 dark:text-rose-400",
    soft: "bg-rose-50 dark:bg-rose-950/40",
    border: "border-rose-200 dark:border-rose-900/50",
    solid: "bg-rose-500",
  },
  emerald: {
    fg: "text-emerald-600 dark:text-emerald-400",
    soft: "bg-emerald-50 dark:bg-emerald-950/40",
    border: "border-emerald-200 dark:border-emerald-900/50",
    solid: "bg-emerald-500",
  },
  violet: {
    fg: "text-violet-600 dark:text-violet-400",
    soft: "bg-violet-50 dark:bg-violet-950/40",
    border: "border-violet-200 dark:border-violet-900/50",
    solid: "bg-violet-500",
  },
  teal: {
    fg: "text-teal-600 dark:text-teal-400",
    soft: "bg-teal-50 dark:bg-teal-950/40",
    border: "border-teal-200 dark:border-teal-900/50",
    solid: "bg-teal-500",
  },
  sky: {
    fg: "text-sky-600 dark:text-sky-400",
    soft: "bg-sky-50 dark:bg-sky-950/40",
    border: "border-sky-200 dark:border-sky-900/50",
    solid: "bg-sky-500",
  },
} as const satisfies Record<string, Accent>;

export type AccentName = keyof typeof ACCENTS;

export interface SectionIdentity {
  icon: LucideIcon;
  /** Title-cased section label. */
  label: string;
  /** One-line "what this section is for" used as the section subtitle. */
  blurb: string;
  accent: AccentName;
}

/**
 * Per-section identity. Order mirrors the canonical EOS agenda. IDS is the
 * heart of the meeting, so it carries the primary olive; the reporting scan
 * (segue→todos) gets a warm-to-cool spread so the stepper reads as progress.
 */
export const SECTION_IDENTITY: Record<AgendaKind, SectionIdentity> = {
  segue: {
    icon: Coffee,
    label: "Check-in",
    blurb: "Good news round — personal and professional wins from the week.",
    accent: "amber",
  },
  scorecard: {
    icon: BarChart3,
    label: "Scorecard",
    blurb: "Weekly measurables, on-track or off. Off-track drops to Issues.",
    accent: "blue",
  },
  rocks: {
    icon: Mountain,
    label: "Rocks",
    blurb: "90-day priorities — on-track or off. Off-track drops to Issues.",
    accent: "forest",
  },
  headlines: {
    icon: Newspaper,
    label: "Headlines",
    blurb: "Customer and employee news. Celebrate, or drop to Issues.",
    accent: "brown",
  },
  todos: {
    icon: ListChecks,
    label: "To-Dos",
    blurb: "Last week's 7-day commitments — done or not. Aim for 90%.",
    accent: "rose",
  },
  ids: {
    icon: Lightbulb,
    label: "Issues",
    blurb: "Go deep on the most important issues and solve them for good.",
    accent: "olive",
  },
  conclude: {
    icon: Flag,
    label: "Conclude",
    blurb: "Recap to-dos, send cascading messages, rate the meeting.",
    accent: "emerald",
  },
};

export interface ItemIdentity {
  icon: LucideIcon;
  /** Singular noun, title case — "Issue", "To-Do", "Headline", "Cascade". */
  noun: string;
  /** Imperative button verb — "Add to Issues", "Add To-Do", etc. */
  verb: string;
  accent: AccentName;
}

/** Per-item-type identity for the composer switcher + create affordances. */
export const ITEM_IDENTITY: Record<MeetingItemType, ItemIdentity> = {
  issue: { icon: Lightbulb, noun: "Issue", verb: "Add to Issues", accent: "amber" },
  todo: { icon: ListChecks, noun: "To-Do", verb: "Add To-do", accent: "rose" },
  headline: { icon: Newspaper, noun: "Headline", verb: "Post Headline", accent: "brown" },
  cascade: { icon: Send, noun: "Cascade", verb: "Send Cascade", accent: "violet" },
};
